import { useState, useMemo, useCallback, useRef, useEffect, useContext } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Settings, Search, X, ChevronRight } from "lucide-react";
import { AuthContext } from "../../Moduels/Context/AuthContext";
import {
  SETTINGS_CATEGORIES,
  flattenSettingsItems,
} from "../../config/settingsNavigation";
import { consumeSettingsReturnPath } from "./settingsExit";
import { markSettingsPathVisited } from "./settingsVisited";
import BackButton from "../common/BackButton";
import "../../styles/settingsWorkspace.css";

const ALL_ITEMS = flattenSettingsItems();

function resolveActiveCategory(pathname, search) {
  for (const cat of SETTINGS_CATEGORIES) {
    for (const item of cat.items) {
      const itemPath = item.path || "";
      const qIdx = itemPath.indexOf("?");
      const itemBase = (qIdx >= 0 ? itemPath.slice(0, qIdx) : itemPath).replace(/\/+$/, "") || "/";
      const normPath = pathname.replace(/\/+$/, "") || "/";
      if (normPath === itemBase || normPath.startsWith(itemBase + "/")) {
        if (qIdx >= 0) {
          const itemParams = new URLSearchParams(itemPath.slice(qIdx + 1));
          const locParams = new URLSearchParams(search || "");
          let allMatch = true;
          for (const [key, val] of itemParams.entries()) {
            if (locParams.get(key) !== val) { allMatch = false; break; }
          }
          if (allMatch) return { categoryId: cat.id, itemId: item.id, item };
        } else {
          return { categoryId: cat.id, itemId: item.id, item };
        }
      }
    }
  }
  return { categoryId: null, itemId: null, item: null };
}

export default function SettingsLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user: me } = useContext(AuthContext);

  const isHome = location.pathname === "/settings" && !location.search.includes("tab=");
  const pathName = location.pathname.replace(/\/+$/, "") || "/";
  const wideContent = pathName === "/settings/attendance/comp-off" || pathName === "/settings/leave/encashment";

  const { categoryId: activeCatId, item: activeItem } = useMemo(
    () => resolveActiveCategory(location.pathname, location.search),
    [location.pathname, location.search]
  );

  const activeCat = activeCatId
    ? SETTINGS_CATEGORIES.find((c) => c.id === activeCatId)
    : null;

  useEffect(() => {
    if (activeItem?.path) {
      markSettingsPathVisited(activeItem.path);
      window.dispatchEvent(new Event("brixigo-settings-visited"));
    }
  }, [activeItem?.path]);

  // Optional trailing crumb a page can push (e.g. "Add Leave Type") via Outlet context;
  // the page clears it on unmount.
  const [extraCrumb, setExtraCrumb] = useState(null);
  const outletContext = useMemo(() => ({ setExtraCrumb }), []);

  const [query, setQuery] = useState("");
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);

  const searchResults = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    return ALL_ITEMS.filter(
      (it) =>
        it.label.toLowerCase().includes(q) ||
        (it.description || "").toLowerCase().includes(q) ||
        (it.categoryLabel || "").toLowerCase().includes(q)
    ).slice(0, 10);
  }, [query]);

  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const orgName = me?.organisation?.name || me?.user?.organisation?.name || "BrixiGo Technologies Pvt. Ltd.";

  const closeSettings = useCallback(() => {
    navigate(consumeSettingsReturnPath(), { replace: true });
  }, [navigate]);

  const openSetting = useCallback((path) => {
    markSettingsPathVisited(path);
    window.dispatchEvent(new Event("brixigo-settings-visited"));
    navigate(path);
  }, [navigate]);

  return (
    <div className="sw-shell">
      {isHome && (
        <header className="sw-header">
          <div className="sw-header-left">
            <div className="sw-header-icon">
              {typeof Settings === "function" || Settings?.$$typeof ? <Settings size={18} /> : null}
            </div>
            <div className="sw-header-title-group">
              <span className="sw-header-title">All Settings</span>
              <span className="sw-header-sub">{orgName}</span>
            </div>
          </div>

          <div className="sw-header-center" ref={searchRef}>
            <div className="sw-search-wrap">
              <Search size={15} className="sw-search-icon" />
              <input
                className="sw-search-input"
                type="text"
                placeholder="Search settings ( / )"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setShowResults(true);
                }}
                onFocus={() => query.trim() && setShowResults(true)}
              />
              {showResults && query.trim() && (
                <div className="sw-search-results">
                  {searchResults.length ? (
                    searchResults.map((item) => (
                      <button
                        key={item.id}
                        className="sw-search-item"
                        onClick={() => {
                          openSetting(item.path);
                          setQuery("");
                          setShowResults(false);
                        }}
                      >
                        {typeof item.icon === "function" || item.icon?.$$typeof ? <item.icon size={15} /> : null}
                        <span>{item.label}</span>
                        <span className="sw-search-item-cat">{item.categoryLabel}</span>
                      </button>
                    ))
                  ) : (
                    <div className="sw-search-empty">No settings found for &ldquo;{query}&rdquo;</div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="sw-header-right">
            <button
              type="button"
              className="sw-close-btn"
              onClick={closeSettings}
              aria-label="Close settings and return to the app"
            >
              <span>Close Settings</span>
              <X size={15} aria-hidden />
            </button>
          </div>
        </header>
      )}

      <div className="sw-body">
        <div className={`sw-content${isHome ? " sw-content--home" : ""}`}>
          {!isHome && (
            <div className={`sw-content-inner${wideContent ? " sw-content-inner--wide" : ""}`}>
              {/* Full-page forms (e.g. Add Leave Type) carry their own "Back to …" link. */}
              {!extraCrumb && <BackButton />}
              <div className="sw-breadcrumbs">
                <button
                  type="button"
                  className="sw-breadcrumb-link"
                  onClick={() => navigate("/settings")}
                >
                  All Settings
                </button>
                {activeCat && (
                  <>
                    <ChevronRight size={12} className="sw-breadcrumb-sep" />
                    <span className="sw-breadcrumb-current">
                      {activeCat.label}
                    </span>
                  </>
                )}
                {activeItem && activeCat && (
                  <>
                    <ChevronRight size={12} className="sw-breadcrumb-sep" />
                    {extraCrumb ? (
                      <button
                        type="button"
                        className="sw-breadcrumb-link"
                        onClick={() => navigate(activeItem.path)}
                      >
                        {activeItem.label}
                      </button>
                    ) : (
                      <span className="sw-breadcrumb-current">
                        {activeItem.label}
                      </span>
                    )}
                  </>
                )}
                {extraCrumb && activeItem && activeCat && (
                  <>
                    <ChevronRight size={12} className="sw-breadcrumb-sep" />
                    <span className="sw-breadcrumb-current">{extraCrumb}</span>
                  </>
                )}
              </div>
              <Outlet context={outletContext} />
            </div>
          )}
          {isHome && <Outlet context={outletContext} />}
        </div>
      </div>
    </div>
  );
}
